import { inject, Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { gapi } from 'gapi-script';
import { BehaviorSubject, Observable } from 'rxjs';
import { PathConstants } from '../../db.paths';
;

interface GoogleCalendarCredential {
  clientId: string;
  apiKey: string;
}

interface GoogleCalendarEvent {
  summary: string;
  start: { dateTime: string; timeZone: string; };
  end: { dateTime: string; timeZone: string; };
  description?: string;
  location?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleCalendarService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);

  private isSignedInSubject = new BehaviorSubject<boolean>(false);
  isSignedIn$: Observable<boolean> = this.isSignedInSubject.asObservable();

  private autoSyncSettingSubject = new BehaviorSubject<boolean>(false);
  autoSyncSetting$: Observable<boolean> = this.autoSyncSettingSubject.asObservable();

  private credentials: GoogleCalendarCredential | null = null;
  private readonly DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'];
  private readonly SCOPES = 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile';

  constructor() {
    // Initialize sign-in status and auto-sync setting from local storage
    this.isSignedInSubject.next(localStorage.getItem('googleSignInStatus') === 'true');
    this.autoSyncSettingSubject.next(localStorage.getItem('googleAutoSyncSetting') === 'true');

    // Fetch credentials and initialize gapi client
    this.fetchGoogleApiCredentials().then(credentials => {
      this.credentials = credentials;
      this.initClient(credentials);
    }).catch(error => {
      console.error('Failed to fetch Google API credentials:', error);
    });

    // Listen to Firebase Auth state changes to update isSignedIn status
    authState(this.auth).subscribe(user => {
      if (!user) {
        this.isSignedInSubject.next(false);
        // Clear local storage on sign out
        localStorage.removeItem('googleSignInStatus');
        localStorage.removeItem('googleAutoSyncSetting');
      }
    });
  }

  private async fetchGoogleApiCredentials(): Promise<GoogleCalendarCredential> {
    const docRef = doc(this.firestore, PathConstants.getAppCredential());
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data && data['clientId'] && data['secret']) {
        return { clientId: data['clientId'], apiKey: data['secret'] };
      } else {
        throw new Error('Google API credentials not found or incomplete in Firestore.');
      }
    } else {
      throw new Error('Google API credentials document not found in Firestore.');
    }
  }

  private async initClient(config: GoogleCalendarCredential): Promise<void> {
    try {
      await new Promise<void>((resolve, reject) => {
        gapi.load('client:auth2', { callback: resolve, onerror: reject });
      });

      await gapi.client.init({
        apiKey: config.apiKey,
        clientId: config.clientId,
        discoveryDocs: this.DISCOVERY_DOCS,
        scope: this.SCOPES,
      });

      console.log('Google API client initialized successfully.');

      const authInstance = gapi.auth2.getAuthInstance();
      this.updateSigninStatus(authInstance.isSignedIn.get());
      authInstance.isSignedIn.listen(this.updateSigninStatus.bind(this));

    } catch (error: any) {
      console.error('Error initializing Google API client:', error);
      // TODO: Add user feedback via ToastService
      throw error;
    }
  }

  private updateSigninStatus(isSignedIn: boolean): void {
    this.isSignedInSubject.next(isSignedIn);
    localStorage.setItem('googleSignInStatus', String(isSignedIn));
  }

  async signIn(): Promise<void> {
    try {
      await gapi.auth2.getAuthInstance().signIn();
      console.log('User signed in successfully to Google Calendar.');
      // TODO: Add user feedback via ToastService
    } catch (error: any) {
      console.error('Error signing in to Google Calendar:', error);
      // TODO: Add user feedback via ToastService
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      await gapi.auth2.getAuthInstance().signOut();
      console.log('User signed out successfully from Google Calendar.');
      this.isSignedInSubject.next(false);
      this.autoSyncSettingSubject.next(false);
      localStorage.removeItem('googleSignInStatus');
      localStorage.removeItem('googleAutoSyncSetting');
      // TODO: Add user feedback via ToastService
    } catch (error: any) {
      console.error('Error signing out from Google Calendar:', error);
      // TODO: Add user feedback via ToastService
      throw error;
    }
  }

  isSignedIn(): Observable<boolean> {
    return this.isSignedInSubject.asObservable();
  }

  saveAutoSyncSetting(enabled: boolean): void {
    localStorage.setItem('googleAutoSyncSetting', String(enabled));
    this.autoSyncSettingSubject.next(enabled);
    console.log('GoogleCalendarService: Auto-sync setting saved:', enabled);
  }

  getAutoSyncSetting(): boolean {
    return this.autoSyncSettingSubject.value;
  }

  async createOrUpdateEvent(event: any): Promise<string> {
    if (!gapi.client || !(gapi.client as any).calendar) {
      throw new Error('Google API client not loaded or calendar service not available.');
    }
    try {
      let response: any;
      if (event.meta.googleEventId) {
        response = await (gapi.client as any).calendar.events.update({
          calendarId: 'primary',
          eventId: event.meta.googleEventId,
          resource: event
        });
        console.log('Google Calendar event updated:', response);
      } else {
        response = await (gapi.client as any).calendar.events.insert({
          calendarId: 'primary',
          resource: event
        });
        console.log('Google Calendar event created:', response);
      }
      return response.result.id;
    } catch (error: any) {
      console.error('Error creating or updating Google Calendar event:', error);
      throw error;
    }
  }

  async deleteEvent(googleEventId: string): Promise<void> {
    if (!gapi.client || !(gapi.client as any).calendar) {
      throw new Error('Google API client not loaded or calendar service not available.');
    }
    try {
      await (gapi.client as any).calendar.events.delete({
        calendarId: 'primary',
        eventId: googleEventId
      });
      console.log(`Google Calendar event ${googleEventId} deleted.`);
    } catch (error: any) {
      console.error('Error deleting Google Calendar event:', error);
      throw error;
    }
  }

  // Optional: list upcoming events (for testing/display)
  async listUpcomingEvents(): Promise<any> {
    if (!gapi.client || !(gapi.client as any).calendar) {
      throw new Error('Google API client not loaded or calendar service not available.');
    }
    try {
      const response = await (gapi.client as any).calendar.events.list({
        calendarId: 'primary',
        timeMin: (new Date()).toISOString(),
        showDeleted: false,
        singleEvents: true,
        maxResults: 10,
        orderBy: 'startTime'
      });
      console.log('Upcoming events:', response);
      return response.result.items;
    } catch (error: any) {
      console.error('Error fetching upcoming events:', error);
      throw error;
    }
  }

  ngOnDestroy(): void {
    // No specific gapi cleanup needed here, as gapi is global
    this.isSignedInSubject.complete();
    this.autoSyncSettingSubject.complete();
  }
}